import React, {
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Select,
  Input,
  InputNumber,
  Row,
  Col,
  Typography,
  DatePicker,
} from 'antd';
import SelectCustomers from '@components/SelectCustomers';
import { IObject } from '@utils/defaultTypes';
import IndividualRangePicker from '@components/DatePicker/IndividualRangePicker';
import moment from 'moment';
import SliderRange from '@components/SliderRange';
import SearchUploader from '@components/SearchUploader';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import CustomTag from '@components/CustomTag';
import { toBytes } from '@utils/formatters';
import { FilterQueryItems } from '../types';

type FranchiseUnit = 'KB' | 'MB' | 'GB';

const franchiseUnits: FranchiseUnit[] = ['KB', 'MB', 'GB'];

const getUnitInBytes = (unit: FranchiseUnit) => toBytes(1, unit);

const getFranchiseValue = (
  value: string | null | undefined,
  unit: FranchiseUnit,
) => {
  if (!value) return undefined;
  return Number(value) / getUnitInBytes(unit);
};

type QueryItemType = {
  clearSearchParams: (paramsToDelete?: string[]) => void;
  setSearchParams: (values: IObject) => void;
  valueOfItem: FilterQueryItems;
  onChangeSearchUploader: (value: {
    [key: string]: string[] | undefined;
  }) => void;
};

const { Search } = Input;

export const QueryItem = ({
  setSearchParams,
  clearSearchParams,
  valueOfItem,
  onChangeSearchUploader,
}: QueryItemType) => {
  const selectedSecondValue = useMemo(
    () => (valueOfItem?.rules ? valueOfItem.rules[0] : undefined),
    [valueOfItem],
  );
  const { keycloak } = useContext(KeycloakContext);
  const formatItem = useMemo(
    () => (valueOfItem?.rules?.length === 2 ? valueOfItem.rules[1] : undefined),
    [valueOfItem],
  );
  const [selectedThirdValue, setSelectedThirdValue] = useState<any | undefined>(
    selectedSecondValue === 'Franchise'
      ? getFranchiseValue(valueOfItem.defaultValue, 'MB')
      : valueOfItem.defaultValue ?? undefined,
  );
  const [franchiseUnit, setFranchiseUnit] = useState<FranchiseUnit>('MB');

  const { data: newApnData } = useSwr(
    valueOfItem.value === 'apn' || valueOfItem.value === 'provisionedApn'
      ? `/service-proxy/apns-customer/${keycloak?.idTokenParsed?.customerId}`
      : null,
  );
  const apnData = useMemo(() => {
    if (!newApnData) return [];
    const uniqueApns = new Set(newApnData);
    return Array.from(uniqueApns).map(apn => ({
      label: apn,
      value: apn,
      filterProps: apn,
    }));
  }, [newApnData]);

  useEffect(() => {
    setSelectedThirdValue(
      selectedSecondValue === 'Franchise'
        ? getFranchiseValue(valueOfItem.defaultValue, franchiseUnit)
        : valueOfItem.defaultValue,
    );
  }, [franchiseUnit, selectedSecondValue, valueOfItem.defaultValue]);

  const applyFranchiseFilter = useCallback(
    (value: number | null, unit = franchiseUnit) => {
      if (value === null) {
        clearSearchParams([valueOfItem.value]);
        return;
      }

      setSearchParams({
        [valueOfItem.value]: toBytes(value, unit),
      });
    },
    [clearSearchParams, franchiseUnit, setSearchParams, valueOfItem.value],
  );

  const handleSearchUploader = useCallback(
    (value: string[]) => {
      const valueObj = {
        [valueOfItem?.value]: value,
      };
      if (value?.length <= 1) {
        setSearchParams(valueObj);
      } else {
        clearSearchParams([valueOfItem?.value]);
        onChangeSearchUploader(valueObj);
      }
    },
    [
      clearSearchParams,
      onChangeSearchUploader,
      setSearchParams,
      valueOfItem?.value,
    ],
  );

  return (
    <Row align="bottom">
      {selectedSecondValue === 'Equals' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>
          {valueOfItem.value === 'iccid' ||
          valueOfItem.value === 'imsi' ||
          valueOfItem.value === 'msisdn' ? (
            <SearchUploader
              columnName={valueOfItem.value?.toUpperCase()}
              onChange={({ target: { value }, nativeEvent: { type } }) => {
                const isEmptyAndClickOnClear = value === '' && type === 'click';
                if (isEmptyAndClickOnClear) {
                  onChangeSearchUploader({
                    [valueOfItem?.value]: undefined,
                  });
                }
                setSelectedThirdValue(value);
              }}
              onSearch={(value: string) => {
                const splitValue = value
                  ?.split(',')
                  ?.filter(item => item !== '');
                handleSearchUploader(splitValue);
              }}
              onUpload={(value: string[]) => {
                handleSearchUploader(value);
                setSelectedThirdValue(value.join(','));
              }}
              style={{
                width: '100%',
              }}
              value={selectedThirdValue}
              allowClear
            />
          ) : (
            <Search
              disabled={!selectedSecondValue}
              value={selectedThirdValue}
              onChange={event => setSelectedThirdValue(event.target.value)}
              onSearch={() => {
                setSearchParams({
                  [`${valueOfItem?.value}`]: selectedThirdValue,
                });
              }}
              allowClear
              style={{
                width: '100%',
              }}
            />
          )}
        </Col>
      )}

      {selectedSecondValue === 'RangeValues' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>

          <SliderRange
            key={valueOfItem?.value}
            value={
              selectedThirdValue
                ? [
                    selectedThirdValue.split(' ')[0],
                    selectedThirdValue.split(' ')[1],
                  ]
                : []
            }
            onClear={() => {
              clearSearchParams([
                `${valueOfItem?.range?.from}`,
                `${valueOfItem?.range?.to}`,
              ]);

              setSelectedThirdValue(undefined);
            }}
            onChange={({
              valueFrom,
              valueTo,
            }: {
              valueFrom: number | undefined;
              valueTo: number | undefined;
            }) => {
              setSearchParams({
                [`${valueOfItem?.range?.from}`]: valueFrom && Number(valueFrom),
                [`${valueOfItem?.range?.to}`]: valueTo && Number(valueTo),
              });
            }}
            format={formatItem ?? ''}
          />
        </Col>
      )}

      {selectedSecondValue === 'Franchise' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>
          <InputNumber
            min={0}
            value={selectedThirdValue}
            onChange={value => setSelectedThirdValue(value)}
            onBlur={() => applyFranchiseFilter(selectedThirdValue)}
            onPressEnter={() => applyFranchiseFilter(selectedThirdValue)}
            addonAfter={
              <Select
                value={franchiseUnit}
                onChange={(unit: FranchiseUnit) => {
                  setFranchiseUnit(unit);
                  applyFranchiseFilter(selectedThirdValue, unit);
                }}
                options={franchiseUnits.map(unit => ({
                  label: unit,
                  value: unit,
                }))}
                style={{ width: 64 }}
              />
            }
            style={{ width: '100%' }}
          />
        </Col>
      )}

      {selectedSecondValue === 'Options' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>
          <Select
            disabled={valueOfItem?.disabled || !selectedSecondValue}
            onSelect={value => {
              setSelectedThirdValue(value);
              setSearchParams({
                [`${valueOfItem?.value}`]: value,
              });
            }}
            value={selectedThirdValue}
            defaultValue={selectedThirdValue}
            style={{ width: '100%' }}
            allowClear
            onClear={() => {
              clearSearchParams([`${valueOfItem?.value}`]);
              setSelectedThirdValue(undefined);
            }}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              const verifyString = (value: any) =>
                String(value).toLowerCase().indexOf(input.toLowerCase()) >= 0;
              // @ts-ignore
              return verifyString(option?.filterProps);
            }}
            options={
              valueOfItem.value === 'apn' ||
              valueOfItem.value === 'provisionedApn'
                ? apnData
                : valueOfItem?.options?.map(option => {
                    return {
                      label: option.label,
                      value: option.value,
                      filterProps: option.label,
                    };
                  })
            }
          />
        </Col>
      )}

      {selectedSecondValue === 'Client' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>
          <div>
            <SelectCustomers
              value={selectedThirdValue && Number(selectedThirdValue)}
              onChange={value => {
                setSelectedThirdValue(value);
                setSearchParams({ customerId: value });
              }}
              allowClear
              isMotBusiness={valueOfItem?.isMotBusiness}
            />
          </div>
        </Col>
      )}

      {selectedSecondValue === 'DateRange' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>
          <IndividualRangePicker
            onChange={({ startDate, endDate }) => {
              setSearchParams({
                [`${valueOfItem?.date?.start}`]: startDate,
                [`${valueOfItem?.date?.end}`]: endDate,
              });

              setSelectedThirdValue(`${startDate}/${endDate}`);
            }}
            value={
              selectedThirdValue
                ? [
                    selectedThirdValue.split('/')[0],
                    selectedThirdValue.split('/')[1],
                  ]
                : []
            }
            hideFilters={valueOfItem?.value === 'cancellation'}
          />
        </Col>
      )}
      {selectedSecondValue === 'Date' && (
        <Col lg={24} xl={24} xs={24}>
          <Typography>{valueOfItem.name}</Typography>
          <DatePicker
            showTime
            value={
              selectedThirdValue &&
              moment(new Date(selectedThirdValue), 'YYYYMMDD HH:mm:ss')
            }
            format="DD/MM/YYYY HH:mm"
            style={{ width: '100%' }}
            allowClear
            onChange={value => {
              setSelectedThirdValue(value);
              setSearchParams({
                [`${valueOfItem?.value}`]: value ? value.toISOString() : null,
              });
            }}
            renderExtraFooter={() => (
              <div
                style={{
                  display: 'flex',
                  gap: '2px',
                  flexWrap: 'wrap',
                  padding: '8px 0',
                }}
              >
                {valueOfItem?.options?.map(item => (
                  <CustomTag
                    key={`${item.value}`}
                    color="green"
                    style={{
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                    onClick={() => {
                      setSelectedThirdValue(item.value);
                      setSearchParams({
                        [`${valueOfItem?.value}`]: item.value,
                      });
                    }}
                  >
                    {item.label}
                  </CustomTag>
                ))}
              </div>
            )}
          />
        </Col>
      )}
    </Row>
  );
};
